declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

import {
  SafetyInduction,
  SafetyInductionCompletion,
  CreateSafetyInduction,
  SafetyInductionFilters,
  SafetyCompetency,
  CreateSafetyCompetency,
  SafetyCompetencyFilters,
  UserSafetyCompetency,
  CreateUserSafetyCompetency,
  UserSafetyCompetencyFilters,
  SiteSafetyRequirement,
  CreateSiteSafetyRequirement,
  SiteSafetyRequirementFilters,
} from '../types/safety';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

class SafetyService {
  private apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  private buildQuery(params: Record<string, any>) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    const query = searchParams.toString();
    return query ? `?${query}` : '';
  }

  inductions = {
    async list(filters: SafetyInductionFilters = {}): Promise<SafetyInduction[]> {
      const query = this.buildQuery(filters);
      const response = await this.apiCall(`/safety/inductions/${query}`);
      return response.results || response;
    },

    async create(data: CreateSafetyInduction): Promise<SafetyInduction> {
      return this.apiCall('/safety/inductions/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async update(id: number, data: Partial<CreateSafetyInduction>): Promise<SafetyInduction> {
      return this.apiCall(`/safety/inductions/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async complete(id: number, acknowledged = true): Promise<SafetyInductionCompletion> {
      return this.apiCall(`/safety/inductions/${id}/complete/`, {
        method: 'POST',
        body: JSON.stringify({ acknowledged }),
      });
    },

    async completions(id: number): Promise<SafetyInductionCompletion[]> {
      return this.apiCall(`/safety/inductions/${id}/completions/`);
    },
  };

  competencies = {
    async list(filters: SafetyCompetencyFilters = {}): Promise<SafetyCompetency[]> {
      const query = this.buildQuery(filters);
      const response = await this.apiCall(`/safety/competencies/${query}`);
      return response.results || response;
    },

    async create(data: CreateSafetyCompetency): Promise<SafetyCompetency> {
      return this.apiCall('/safety/competencies/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async update(id: number, data: Partial<CreateSafetyCompetency>): Promise<SafetyCompetency> {
      return this.apiCall(`/safety/competencies/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
  };

  userCompetencies = {
    async list(filters: UserSafetyCompetencyFilters = {}): Promise<UserSafetyCompetency[]> {
      const query = this.buildQuery(filters);
      const response = await this.apiCall(`/safety/user-competencies/${query}`);
      return response.results || response;
    },

    async create(data: CreateUserSafetyCompetency): Promise<UserSafetyCompetency> {
      return this.apiCall('/safety/user-competencies/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async update(id: number, data: Partial<CreateUserSafetyCompetency>): Promise<UserSafetyCompetency> {
      return this.apiCall(`/safety/user-competencies/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
  };

  siteRequirements = {
    async list(filters: SiteSafetyRequirementFilters = {}): Promise<SiteSafetyRequirement[]> {
      const query = this.buildQuery(filters);
      const response = await this.apiCall(`/safety/site-requirements/${query}`);
      return response.results || response;
    },

    async create(data: CreateSiteSafetyRequirement): Promise<SiteSafetyRequirement> {
      return this.apiCall('/safety/site-requirements/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async update(id: number, data: Partial<CreateSiteSafetyRequirement>): Promise<SiteSafetyRequirement> {
      return this.apiCall(`/safety/site-requirements/${id}/`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },
  };
}

export default new SafetyService();
